import React, { useEffect } from 'react';
import { GoodsReceiptRecord } from '@/types/domain/grn-management';
import { Button } from '@/components/ui/Button';
import { Database, RefreshCw, X, ShieldCheck } from 'lucide-react';

interface GrnSapSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSync: () => Promise<void>;
  grn: GoodsReceiptRecord;
  isSubmitting?: boolean;
}

export const GrnSapSyncModal: React.FC<GrnSapSyncModalProps> = ({
  isOpen,
  onClose,
  onConfirmSync,
  grn,
  isSubmitting = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs animate-in fade-in"
        onClick={() => !isSubmitting && onClose()}
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-xl rounded-2xl p-6 bg-card border border-border shadow-2xl z-10 space-y-4"
      >
        <div className="flex items-start justify-between border-b border-border/60 pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-600">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-foreground">
                SAP S/4HANA MIGO Goods Receipt (Movement 101)
              </h3>
              <p className="text-xs text-foreground-secondary">
                Post official warehouse material document into SAP ERP central inventory ledger.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-foreground-muted hover:text-foreground p-1 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-3.5 rounded-xl border border-border/70 bg-surface/50 space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">ProcureAI GRN #:</span>
            <span className="font-extrabold text-primary font-mono">{grn.grnNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Linked PO Document #:</span>
            <span className="font-bold text-foreground font-mono">{grn.poNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Supplier / Vendor:</span>
            <span className="font-bold text-foreground">{grn.vendorName} ({grn.vendorCode})</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Destination Plant & SLoc:</span>
            <span className="font-mono text-purple-700 font-bold">{grn.plantCode} / {grn.storageLocation}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-secondary font-medium">Net Accepted Quantity:</span>
            <span className="font-mono font-black text-emerald-600">{grn.totalAcceptedQuantity} units</span>
          </div>
        </div>

        <div className="p-3 rounded-xl border border-border/80 bg-slate-900 text-slate-100 font-mono text-[11px] overflow-x-auto space-y-1.5">
          <span className="text-slate-400 text-[10px] uppercase font-bold block">// BAPI_GOODSMVT_CREATE Payload</span>
          <pre className="leading-relaxed">
{JSON.stringify(
  {
    GOODSMVT_HEADER: {
      PSTNG_DATE: grn.receivedDate,
      DOC_DATE: grn.receivedDate,
      REF_DOC_NO: grn.deliveryChallanNumber,
      PR_UNAME: grn.receiverName,
    },
    GOODSMVT_CODE: { GM_CODE: '01' }, // MB01 / MIGO Goods Receipt
    GOODSMVT_ITEM: grn.items.map((it) => ({
      PO_NUMBER: grn.poNumber,
      PO_ITEM: it.lineNumber,
      MOVE_TYPE: '101', // Standard GR into warehouse
      ENTRY_QNT: it.acceptedQuantity,
      ENTRY_UOM: it.uom,
      PLANT: grn.plantCode,
      STGE_LOC: it.storageLocation,
    })),
  },
  null,
  2
)}
          </pre>
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-border/40">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting} className="rounded-xl font-bold">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onConfirmSync}
            disabled={isSubmitting}
            className="rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-2xs"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Posting to SAP MIGO...
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4" /> Post SAP Goods Receipt
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
